import { zodResolver } from '@hookform/resolvers/zod';
import { styled, TextField, Typography, type DialogProps } from '@mui/material';
import Button from 'components/Button';
import CurrencySelector from 'components/CurrencySelector';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import PageSection from 'components/new/PageSection';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import CountrySelector from 'further-ui/components/CountrySelector';
import FormField from 'further-ui/components/FormField';
import HorizontalStepper from 'further-ui/components/HorizontalStepper';
import { Column, Row } from 'further-ui/components/Layout';
import { bankReconciliation as rules } from 'further-ui/validations';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useState } from 'react';
import { Control, Controller, FieldPath, useForm } from 'react-hook-form';
import { z } from 'zod';

const STEP_FIELDS: Array<
  Array<FieldPath<z.infer<typeof rules.AddBankAccountInput>>>
> = [
  [
    'bankAccount.bankName',
    'bankAccount.accountName',
    'bankAccount.accountCurrency',
    'bankAccount.beneficiaryName',
    'bankAccount.iban',
    'bankAccount.accountNumber',
    'bankAccount.sortCode',
    'bankAccount.swift',
  ],
  [
    'bankAccount.additionalBankCode',
    'bankAccount.customPaymentReference',
    'bankAccount.additionalPaymentInformation',
  ],
  [],
];

const formSchema = rules.AddBankAccountInput;

type FormValues = z.infer<typeof formSchema>;

const Field: React.FC<{
  label: string;
  name: FieldPath<FormValues>;
  control: Control<FormValues>;
  tooltipText?: string;
  placeholder?: string;
  rows?: number;
}> = ({ label, control, name, tooltipText, placeholder, rows }) => (
  <FormField label={label} tooltip={tooltipText} spacing="xs">
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          variant="outlined"
          multiline={!!rows}
          size="small"
          fullWidth
          rows={rows}
          {...field}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  </FormField>
);

const Body2Column = styled(Column)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.grey[900],
}));

const STEP_TITLES = [
  'Account details',
  'Other account details',
  'Account address',
];

type Props = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  transitionDuration?: DialogProps['transitionDuration'];
};

const ManualAdd: React.FC<Props> = ({
  open,
  onClose,
  onCancel,
  transitionDuration,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { selectedVehicleId } = useLayoutContext();

  const mutationHandler = useHandleTrpcMutation()({
    successMessage: 'Bank account added.',
    onSuccess: () => {
      onClose();
    },
  });

  const addBankAccount =
    api.bankAccount.addBankAccount.useMutation(mutationHandler);

  const { vehicle } = useVehicle(selectedVehicleId);

  const { control, handleSubmit, setValue, trigger } = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    values: {
      bankAccount: {
        accountName: '',
        bankName: '',
        iban: '',
        accountCurrency: vehicle?.baseCurrency ?? CurrencyCode.GBP,
        accountAddress: {
          address1: '',
          city: '',
          postcode: '',
          country: '',
        },
      },
    },
    criteriaMode: 'all',
  });

  const onSubmit = (data: FormValues) => {
    addBankAccount.mutate({
      vehicleId: selectedVehicleId ?? '',
      ...data,
    });
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onExited={() => setCurrentStep(0)}
      transitionDuration={transitionDuration}
      title="Add account manually"
      maxWidth="md"
      fullHeight
      customActions={
        <Row fullWidth justify="space-between">
          <Button
            variant="outlined"
            size="large"
            onClick={
              currentStep > 0 ? () => setCurrentStep((s) => s - 1) : onCancel
            }
          >
            {currentStep > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Row spacing="sm">
            {currentStep >= 1 && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleSubmit(onSubmit)}
                loading={addBankAccount.isPending}
              >
                Skip for now
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={
                currentStep === 2
                  ? handleSubmit(onSubmit)
                  : async () => {
                      const valid = await trigger(STEP_FIELDS[currentStep]);
                      if (valid) setCurrentStep((s) => s + 1);
                    }
              }
              loading={addBankAccount.isPending}
            >
              {currentStep === 2 ? 'Add account' : 'Next'}
            </Button>
          </Row>
        </Row>
      }
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Column fullWidth spacing="lg">
            <PageSection
              spacing="sm"
              title={
                <Typography variant="h6">
                  You may want to do this if:
                </Typography>
              }
            >
              <Body2Column spacing="xs">
                <ColorIndicatorWithText
                  size={6}
                  color="black"
                  text="Your bank doesn't currently provide a bank feed."
                />
                <ColorIndicatorWithText
                  size={6}
                  color="black"
                  text="Your bank doesn't provide a bank feed for your account type."
                />
                <ColorIndicatorWithText
                  size={6}
                  color="black"
                  text="You need a petty cash account."
                />
              </Body2Column>
              <Typography variant="body2">
                Transactions downloaded from your bank can be manually imported
                into this account.
              </Typography>
            </PageSection>
            <PageSection
              spacing="lg"
              title={
                <Row
                  centered
                  fullWidth
                  breakpoint="sm"
                  spacing="sm"
                  justify="space-between"
                >
                  <Typography variant="inherit">
                    {STEP_TITLES[currentStep]}
                  </Typography>
                  <HorizontalStepper
                    currentStep={currentStep}
                    numberOfSteps={3}
                    color="primary"
                  />
                </Row>
              }
              withoutBottomDivider
            >
              {currentStep === 0 && (
                <Column spacing="lg" fullWidth>
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <Field
                      control={control}
                      name="bankAccount.bankName"
                      label="Bank name"
                      placeholder="Enter bank name"
                    />
                    <Field
                      control={control}
                      name="bankAccount.accountName"
                      label="Account description"
                      placeholder="Enter account description"
                      tooltipText="This should be a unique name used to refer to this account in the system."
                    />
                  </Row>
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <FormField label="Account currency" spacing="xs">
                      <Controller
                        control={control}
                        name="bankAccount.accountCurrency"
                        render={({
                          field: { value, ...rest },
                          fieldState: { error },
                        }) => (
                          <CurrencySelector
                            {...rest}
                            error={error}
                            placeholder="Select currency"
                            value={value}
                          />
                        )}
                      />
                    </FormField>
                    <Field
                      control={control}
                      name="bankAccount.beneficiaryName"
                      label="Account holder name"
                      placeholder="Enter account holder name"
                    />
                  </Row>

                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <Field
                      control={control}
                      name="bankAccount.iban"
                      label="IBAN"
                      placeholder="Enter IBAN"
                    />
                    <Field
                      control={control}
                      name="bankAccount.accountNumber"
                      label="Account number"
                      placeholder="Enter account number"
                    />
                  </Row>
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <Field
                      control={control}
                      name="bankAccount.sortCode"
                      label="Sort/ABA/ACH code"
                      placeholder="Enter code"
                    />
                    <Field
                      control={control}
                      name="bankAccount.swift"
                      label="SWIFT/BIC"
                      placeholder="Enter code"
                    />
                  </Row>
                </Column>
              )}
              {currentStep === 1 && (
                <Column fullWidth spacing="lg">
                  <Row fullWidth spacing="lg" breakpoint="sm">
                    <Field
                      control={control}
                      name="bankAccount.additionalBankCode"
                      label="Additional bank code"
                      placeholder="Enter code"
                    />
                    <Field
                      tooltipText="By default, the system will create an automated unique payment reference for each investor. If you wish to use a fixed payment reference for all investors, overriding the system reference, please input it here."
                      control={control}
                      name="bankAccount.customPaymentReference"
                      label="Payment reference"
                      placeholder="Enter reference"
                    />
                  </Row>
                  <Field
                    tooltipText="Write any payment guidance here. If no guidance is required, leave this field blank."
                    control={control}
                    name="bankAccount.additionalPaymentInformation"
                    rows={10}
                    label="Payment information"
                    placeholder="Enter information"
                  />
                </Column>
              )}
              {currentStep === 2 && (
                <Column fullWidth spacing="lg">
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <Field
                      control={control}
                      name="bankAccount.accountAddress.address1"
                      label="Address line 1"
                      placeholder="Enter address line 1"
                    />
                    <Field
                      control={control}
                      name="bankAccount.accountAddress.address2"
                      label="Address line 2"
                      placeholder="Enter address line 2"
                    />
                  </Row>
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <Field
                      control={control}
                      name="bankAccount.accountAddress.city"
                      label="Town/City"
                      placeholder="Enter town/city"
                    />
                    <Field
                      control={control}
                      name="bankAccount.accountAddress.postcode"
                      placeholder="Enter postal code"
                      label="Postal code"
                    />
                  </Row>
                  <Row spacing="lg" breakpoint="sm" fullWidth>
                    <FormField label="Country" spacing="xs">
                      <Controller
                        control={control}
                        name="bankAccount.accountAddress.country"
                        render={({
                          field: { value, ref, ...rest },
                          fieldState: { error },
                        }) => (
                          <CountrySelector
                            {...rest}
                            ref={ref}
                            value={value}
                            name="bankAccount.accountAddress.country"
                            placeholder="Select country"
                            fullWidth
                            error={error}
                            onChange={(value) => {
                              setValue(
                                'bankAccount.accountAddress.country',
                                value ?? '',
                              );
                            }}
                          />
                        )}
                      />
                    </FormField>
                    <FormField />
                  </Row>
                </Column>
              )}
            </PageSection>
          </Column>
        </form>
      }
    />
  );
};

export default ManualAdd;
